import { grpcClientFactory } from './grpcClientFactory';
import { GrpcCallError } from '../utils/grpcErrors';
const SCOPE_ARTIFACT_TYPE_SYSTEM = 1;
const SCOPE_ARTIFACT_TYPE_USER = 2;
function artifactTypeToProto(type) {
    return type === 'system' ? SCOPE_ARTIFACT_TYPE_SYSTEM : SCOPE_ARTIFACT_TYPE_USER;
}
function protoToArtifactType(protoType) {
    return protoType === SCOPE_ARTIFACT_TYPE_SYSTEM ? 'system' : 'user';
}
function protoCameraToTs(cam) {
    if (!cam)
        return { latitude: 0, longitude: 0, height: 0, heading: 0, pitch: 0, roll: 0 };
    return {
        latitude: cam.getLatitude(),
        longitude: cam.getLongitude(),
        height: cam.getHeight(),
        heading: cam.getHeading(),
        pitch: cam.getPitch(),
        roll: cam.getRoll(),
    };
}
function protoLayerConfigToTs(lc) {
    return {
        artifactId: lc.getArtifactId(),
        visible: lc.getVisible(),
        opacity: lc.getOpacity(),
    };
}
function protoScopeArtifactConfigToTs(ac) {
    return {
        id: ac.getId(),
        artifactType: protoToArtifactType(ac.getArtifactType()),
        artifactId: ac.getArtifactId(),
        visible: ac.getVisible(),
        opacity: ac.getOpacity(),
        colorOverride: ac.getColorOverride() || undefined,
        viewState: ac.getViewState() || undefined,
        sortOrder: ac.getSortOrder(),
    };
}
function protoScopeToTs(protoScope) {
    return {
        id: protoScope.getId(),
        projectId: protoScope.getProjectId(),
        name: protoScope.getName(),
        cameraPosition: protoCameraToTs(protoScope.getCameraPosition()),
        layerConfigs: (protoScope.getLayerConfigsList() || []).map(protoLayerConfigToTs),
        artifactConfigs: (protoScope.getArtifactConfigsList() || []).map(protoScopeArtifactConfigToTs),
        sortOrder: protoScope.getSortOrder(),
        createdAt: protoScope.getCreatedAt(),
        updatedAt: protoScope.getUpdatedAt(),
    };
}
function buildCameraPositionProto(cam) {
    const CameraPos = proto.landscope.mapviewer.v1.CameraPosition;
    const p = new CameraPos();
    p.setLatitude(cam.latitude);
    p.setLongitude(cam.longitude);
    p.setHeight(cam.height);
    p.setHeading(cam.heading);
    p.setPitch(cam.pitch);
    p.setRoll(cam.roll);
    return p;
}
function buildLayerConfigsProto(configs) {
    const LayerCfg = proto.landscope.mapviewer.v1.LayerConfig;
    return configs.map(lc => {
        const cfg = new LayerCfg();
        cfg.setArtifactId(lc.artifactId);
        cfg.setVisible(lc.visible);
        cfg.setOpacity(lc.opacity);
        return cfg;
    });
}
function buildArtifactConfigsProto(configs) {
    const ArtifactCfg = proto.landscope.mapviewer.v1.ScopeArtifactConfig;
    return configs.map(ac => {
        const cfg = new ArtifactCfg();
        cfg.setArtifactType(artifactTypeToProto(ac.artifactType));
        cfg.setArtifactId(ac.artifactId);
        cfg.setVisible(ac.visible);
        cfg.setOpacity(ac.opacity);
        if (ac.colorOverride)
            cfg.setColorOverride(ac.colorOverride);
        if (ac.viewState)
            cfg.setViewState(ac.viewState);
        cfg.setSortOrder(ac.sortOrder);
        return cfg;
    });
}
class ScopeService {
    createAuthenticatedClient() {
        return grpcClientFactory.createAuthenticatedUserProjectsClient({ enableCompression: true });
    }
    async createScope(request) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.CreateScopeRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(request.projectId);
                grpcReq.setName(request.name);
                grpcReq.setCameraPosition(buildCameraPositionProto(request.cameraPosition));
                grpcReq.setLayerConfigsList(buildLayerConfigsProto(request.layerConfigs));
                grpcReq.setArtifactConfigsList(buildArtifactConfigsProto(request.artifactConfigs));
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.createScope(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to create scope: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({ scopeId: resp.getScopeId() });
                });
            }
            catch (e) {
                reject(new Error(`Failed to create scope: ${e}`));
            }
        });
    }
    async getScopes(projectId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetScopesRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.getScopes(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new GrpcCallError(`Failed to get scopes: ${err.message}`, err.code));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const scopesList = resp.getScopesList() || [];
                    resolve(scopesList.map(protoScopeToTs));
                });
            }
            catch (e) {
                reject(new Error(`Failed to get scopes: ${e}`));
            }
        });
    }
    async getScope(scopeId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetScopeRequest;
                const grpcReq = new Req();
                grpcReq.setScopeId(scopeId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.getScope(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to get scope: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const scope = resp.getScope();
                    if (!scope) {
                        reject(new Error('Scope not found'));
                        return;
                    }
                    resolve(protoScopeToTs(scope));
                });
            }
            catch (e) {
                reject(new Error(`Failed to get scope: ${e}`));
            }
        });
    }
    async updateScope(request) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.UpdateScopeRequest;
                const grpcReq = new Req();
                grpcReq.setScopeId(request.scopeId);
                if (request.name)
                    grpcReq.setName(request.name);
                if (request.cameraPosition) {
                    grpcReq.setCameraPosition(buildCameraPositionProto(request.cameraPosition));
                }
                if (request.layerConfigs) {
                    grpcReq.setLayerConfigsList(buildLayerConfigsProto(request.layerConfigs));
                }
                if (request.artifactConfigs) {
                    grpcReq.setArtifactConfigsList(buildArtifactConfigsProto(request.artifactConfigs));
                }
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.updateScope(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to update scope: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to update scope: ${e}`));
            }
        });
    }
    async deleteScope(scopeId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.DeleteScopeRequest;
                const grpcReq = new Req();
                grpcReq.setScopeId(scopeId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.deleteScope(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to delete scope: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to delete scope: ${e}`));
            }
        });
    }
    async reorderScopes(projectId, scopeIds) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ReorderScopesRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                grpcReq.setScopeIdsList(scopeIds);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.reorderScopes(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to reorder scopes: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to reorder scopes: ${e}`));
            }
        });
    }
}
export const scopeService = new ScopeService();
